import React from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import en from "../../public/locale/en.json";
import es from "../../public/locale/es.json";
import de from "../../public/locale/de.json";
import fr from "../../public/locale/fr.json";
import pt from "../../public/locale/pt.json";
import pl from "../../public/locale/pl.json";
import he from "../../public/locale/he.json";
import nl from "../../public/locale/nl.json";
import hu from "../../public/locale/hu.json";
import id from "../../public/locale/id.json";
import it from "../../public/locale/it.json";
import zh_CN from "../../public/locale/zh_CN.json";
import zh_TW from "../../public/locale/zh_TW.json";
import th from "../../public/locale/th.json";
import tl from "../../public/locale/tl.json";
import ru from "../../public/locale/ru.json";
import el from "../../public/locale/el.json";
import ja from "../../public/locale/ja.json";
import ko from "../../public/locale/ko.json";
import ro from "../../public/locale/ro.json";
import tr from "../../public/locale/tr.json";

export type LocaleType = typeof en;

type LanguageDict = {
  [key: string]: Record<string, string>;
};

const mergeWithFallback = (translations: Record<string, string>): LocaleType =>
  ({
    ...en,
    ...translations,
  }) as LocaleType;

export const LANGUAGES: LanguageDict = {
  en,
  de: mergeWithFallback(de),
  es: mergeWithFallback(es),
  ja: mergeWithFallback(ja),
  ko: mergeWithFallback(ko),
  pt: mergeWithFallback(pt),
  fr: mergeWithFallback(fr),
  he: mergeWithFallback(he),
  hu: mergeWithFallback(hu),
  id: mergeWithFallback(id),
  it: mergeWithFallback(it),
  nl: mergeWithFallback(nl),
  zh_CN: mergeWithFallback(zh_CN),
  zh_TW: mergeWithFallback(zh_TW),
  th: mergeWithFallback(th),
  tl: mergeWithFallback(tl),
  ru: mergeWithFallback(ru),
  el: mergeWithFallback(el),
  pl: mergeWithFallback(pl),
  ro: mergeWithFallback(ro),
  tr: mergeWithFallback(tr),
  tk: mergeWithFallback(tr), // backend sends "tk" for Turkish
};

export const AVAILABLE_LANGUAGES = Object.keys(LANGUAGES);

interface LocaleStore {
  locale: Record<string, string>;
  currentLanguage: string;
  isHydrated: boolean;
  setLocale: (locale: Record<string, string>) => void;
  setCurrentLanguage: (language: string) => void;
  setHydrated: (hydrated: boolean) => void;
}

export const useLocaleStore = create<LocaleStore>()(
  persist(
    (set) => ({
      locale: en, // Initialize with English translations
      currentLanguage: "en", // Track current language separately
      isHydrated: false,
      setLocale: (locale) => set({ locale }),
      setCurrentLanguage: (language) => {
        set({ currentLanguage: language });
        const translations = LANGUAGES[language] || LANGUAGES.en;
        if (translations) {
          set({ locale: translations });
        }
      },
      setHydrated: (hydrated) => set({ isHydrated: hydrated }),
    }),
    {
      name: "fluid-language-storage",
      partialize: (state) => ({ currentLanguage: state.currentLanguage }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Ensure both currentLanguage and locale are synchronized
          const translations = LANGUAGES[state.currentLanguage] || LANGUAGES.en;
          if (translations) {
            state.locale = translations;
          }
          // Mark as hydrated after rehydration
          state.setHydrated(true);
        }
      },
    },
  ),
);

export const useLocale = () => {
  const locale = useLocaleStore((state) => state.locale);
  const currentLanguage = useLocaleStore((state) => state.currentLanguage);
  const isHydrated = useLocaleStore((state) => state.isHydrated);
  const setCurrentLanguage = useLocaleStore(
    (state) => state.setCurrentLanguage,
  );
  const setHydrated = useLocaleStore((state) => state.setHydrated);

  // Mark as hydrated on client side
  React.useEffect(() => {
    setHydrated(true);
  }, [setHydrated]);

  // Ensure locale is synchronized with currentLanguage after hydration
  React.useEffect(() => {
    if (isHydrated) {
      const translations = LANGUAGES[currentLanguage];
      if (translations && locale !== translations) {
        setCurrentLanguage(currentLanguage);
      }
    }
  }, [currentLanguage, locale, setCurrentLanguage, isHydrated]);

  return (key: string, fallback?: string) => {
    return locale[key] || fallback || key;
  };
};
